import { downloadMediaBlob } from '@/core/api-client';
import { logger } from '@/core/logger';
import { platform } from '@/platform';
import type { PlanMedia } from '@/types';

/**
 * Persistent media cache built on `platform.storage`.
 *
 * Storage backend:
 *   - Tizen: tizen.filesystem under wgt-private/storage (real files
 *     in the user-data partition, survive reboot, no eviction).
 *   - Web (dev / simulator): Cache Storage API. Same semantics, just
 *     subject to browser quota eviction under extreme pressure.
 *
 * Architecture (both backends):
 *   - Each media file goes in under the key `media/<id>`.
 *   - In-memory index mirrors the on-disk metadata for O(1) lookups.
 *   - The "ready" gate is committing the index entry — anything not
 *     in the index is invisible to the scheduler.
 *   - LRU eviction keyed by index insertion order, bounded by both
 *     a byte budget AND an item budget.
 *
 * Trust chain on download:
 *   1. fetch over signed HTTPS    (mitm-proof)
 *   2. sha256 verify against plan (server-trusted hash)
 *   3. validate-blob decode-check (no poison pills)
 *   4. persist to platform.storage
 *   5. THEN expose via index — the scheduler can only pick items that
 *      cleared all four steps.
 */

const KEY_PREFIX = 'media/';
const MAX_CACHE_BYTES = 500 * 1024 * 1024;
const MAX_CACHE_ITEMS = 200;
/**
 * If the browser/Tizen filesystem reports usage past this fraction of
 * the platform quota, we evict aggressively even if we're under
 * MAX_CACHE_BYTES. Otherwise the browser starts unilaterally evicting
 * random files — possibly the auth keys / pairing tokens / manifest
 * cache — which is much worse than us picking the oldest media to drop.
 */
const QUOTA_PRESSURE_THRESHOLD = 0.7;
const QUOTA_RECHECK_INTERVAL_MS = 5 * 60 * 1000;

/**
 * Set of media IDs the scheduler considers "in use" — current plan,
 * upcoming campaign, etc. Eviction will avoid these unless we'd
 * otherwise overflow the cache. Updated by the scheduler via
 * `setProtectedMedia()`.
 */
let protectedIds: Set<string> = new Set();

interface IndexEntry {
  /** Live blob URL the <video>/<img> consumes. Created on init/put. */
  url: string;
  bytes: number;
  sha256: string | null;
  cachedAt: number;
}

// Map preserves insertion order → LRU via delete+set on touch.
const index = new Map<string, IndexEntry>();
let totalBytes = 0;
let initPromise: Promise<void> | null = null;

function storageKey(mediaId: string): string {
  return `${KEY_PREFIX}${mediaId}`;
}
function mediaIdFromKey(key: string): string | null {
  return key.startsWith(KEY_PREFIX) ? key.slice(KEY_PREFIX.length) : null;
}

/** Hex of sha256(blob bytes). */
async function sha256Hex(blob: Blob): Promise<string> {
  const buf = await blob.arrayBuffer();
  const digest = await crypto.subtle.digest('SHA-256', buf);
  const bytes = new Uint8Array(digest);
  let s = '';
  for (let i = 0; i < bytes.length; i++) s += bytes[i].toString(16).padStart(2, '0');
  return s;
}

/** Decode-check: catches corrupt files that would skip-loop in playback. */
async function validateBlob(blob: Blob, kind: 'image' | 'video'): Promise<boolean> {
  const url = URL.createObjectURL(blob);
  try {
    if (kind === 'image') {
      const img = new Image();
      await new Promise<void>((res, rej) => {
        img.onload = () => res();
        img.onerror = () => rej(new Error('image decode failed'));
        img.src = url;
      });
    } else {
      const video = document.createElement('video');
      video.muted = true;
      video.preload = 'metadata';
      await new Promise<void>((res, rej) => {
        video.onloadedmetadata = () => res();
        video.onerror = () => rej(new Error('video decode failed'));
        video.src = url;
      });
    }
    return true;
  } catch {
    return false;
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** Idempotent. Safe to call from multiple sites; only the first run does work. */
export function initializeCache(): Promise<void> {
  if (!initPromise) initPromise = doInit();
  return initPromise;
}

async function doInit(): Promise<void> {
  try {
    await platform.storage.init();
    const entries = await platform.storage.list();
    for (const meta of entries) {
      const id = mediaIdFromKey(meta.key);
      if (!id) continue;
      const blob = await platform.storage.get(meta.key);
      if (!blob) continue;
      const url = URL.createObjectURL(blob);
      // The persisted sha256 is encoded in the contentType (workaround
      // for browser/Tizen storage not exposing arbitrary user metadata).
      // We store it as `application/octet-stream;sha256=<hex>` below.
      const sha256 = parseSha256FromContentType(meta.contentType);
      index.set(id, {
        url,
        bytes: meta.size,
        sha256,
        cachedAt: meta.storedAt,
      });
      totalBytes += meta.size;
    }
    logger.info('[MediaCache] Restored from disk', {
      items: index.size,
      mb: (totalBytes / 1024 / 1024).toFixed(1),
      platform: platform.kind,
    });
    // Trim if persistent state is already over the budget at boot.
    await enforceLimits();
    // Periodic quota check — catches the case where storage quota
    // shrinks under us (other apps consume the partition, OS reclaims
    // disk, etc).
    startQuotaProbe();
  } catch (err) {
    logger.error('[MediaCache] Initialize failed', { err: String(err) });
  }
}

function parseSha256FromContentType(ct: string | null): string | null {
  if (!ct) return null;
  const m = ct.match(/sha256=([a-f0-9]{64})/);
  return m ? m[1] : null;
}

function buildContentType(mime: string, sha256: string | null): string {
  return sha256 ? `${mime};sha256=${sha256}` : mime;
}

// ── Index lookups (sync) ───────────────────────────────────────────

export function getCachedUrl(mediaId: string): string | null {
  const entry = index.get(mediaId);
  if (!entry) return null;
  index.delete(mediaId);
  index.set(mediaId, entry);
  return entry.url;
}

export function getCachedSha256(mediaId: string): string | null | undefined {
  const entry = index.get(mediaId);
  if (!entry) return undefined;
  return entry.sha256;
}

export function getAllCachedPaths(): Map<string, string> {
  const out = new Map<string, string>();
  for (const [id, entry] of index) out.set(id, entry.url);
  return out;
}

export function getAllCachedIds(): Set<string> {
  return new Set(index.keys());
}

// ── Download + persist (the "ready" gate) ──────────────────────────

export async function downloadMedia(media: PlanMedia): Promise<string> {
  await initializeCache();

  const existing = index.get(media.id);
  if (existing && existing.sha256 === media.sha256) {
    index.delete(media.id);
    index.set(media.id, existing);
    return existing.url;
  }
  if (existing) {
    logger.info('[MediaCache] sha256 changed; evicting stale', { id: media.id });
    await evictMedia(media.id);
  }

  logger.info('[MediaCache] Downloading', { id: media.id });
  const blob = await downloadMediaBlob(media.id);

  // 1. Integrity check
  if (media.sha256) {
    const actual = await sha256Hex(blob);
    if (actual !== media.sha256) {
      logger.error('[MediaCache] sha256 mismatch', {
        id: media.id,
        expected: media.sha256,
        actual,
      });
      throw new Error(`sha256 mismatch for ${media.id}`);
    }
  }

  // 2. Decode check
  const renderable = await validateBlob(
    blob,
    media.type === 'video' ? 'video' : 'image',
  );
  if (!renderable) {
    logger.warn('[MediaCache] Rejected corrupt blob', { id: media.id });
    throw new Error(`Corrupt media: ${media.id}`);
  }

  // 3. Persist via the platform layer (Tizen FS or Cache Storage)
  const ct = buildContentType(
    blob.type || 'application/octet-stream',
    media.sha256,
  );
  try {
    await platform.storage.put(storageKey(media.id), blob, ct);
  } catch (err) {
    // Quota-exceeded or transient I/O error — surface and let the caller
    // (sync-manager) treat it as a per-item failure.
    logger.error('[MediaCache] Persist failed', {
      id: media.id,
      err: String(err),
    });
    throw err;
  }

  // 4. Register in the in-memory index — this is the "ready" gate.
  const blobUrl = URL.createObjectURL(blob);
  index.set(media.id, {
    url: blobUrl,
    bytes: blob.size,
    sha256: media.sha256,
    cachedAt: Date.now(),
  });
  totalBytes += blob.size;

  await enforceLimits();

  logger.info('[MediaCache] Cached', {
    id: media.id,
    kb: Math.round(blob.size / 1024),
    totalMb: (totalBytes / 1024 / 1024).toFixed(1),
  });
  return blobUrl;
}

export async function downloadAllMedia(items: PlanMedia[]): Promise<void> {
  await initializeCache();
  for (const item of items) {
    const existing = index.get(item.id);
    if (existing && existing.sha256 === item.sha256) continue;
    try {
      await downloadMedia(item);
    } catch (err) {
      logger.warn('[MediaCache] Skipping failed item', {
        id: item.id,
        err: String(err),
      });
    }
  }
}

// ── Eviction ───────────────────────────────────────────────────────

export async function evictMedia(mediaId: string): Promise<void> {
  const entry = index.get(mediaId);
  if (!entry) return;
  URL.revokeObjectURL(entry.url);
  try {
    await platform.storage.remove(storageKey(mediaId));
  } catch (err) {
    logger.warn('[MediaCache] Disk eviction failed', {
      id: mediaId,
      err: String(err),
    });
    // Even if disk delete failed, drop the index entry — next sync
    // will rewrite a fresh copy if needed.
  }
  totalBytes -= entry.bytes;
  index.delete(mediaId);
}

async function enforceLimits(): Promise<void> {
  // First pass: respect our own MAX_CACHE_BYTES / MAX_CACHE_ITEMS,
  // preferring to evict non-protected entries first. Only evict
  // protected entries if we're STILL over budget after exhausting the
  // unprotected pool.
  await evictUntil(
    () => totalBytes > MAX_CACHE_BYTES || index.size > MAX_CACHE_ITEMS,
    /*allowProtected*/ false,
  );
  await evictUntil(
    () => totalBytes > MAX_CACHE_BYTES || index.size > MAX_CACHE_ITEMS,
    /*allowProtected*/ true,
  );

  // Second pass: respect the underlying platform quota. The 500 MB
  // budget above is a static guess; the real quota varies wildly
  // between Tizen storage partitions and browser allotments. If the
  // platform reports we're past QUOTA_PRESSURE_THRESHOLD, keep evicting
  // — same priority order.
  const quota = await safeGetQuota();
  if (quota && quota.quota > 0) {
    const target = quota.quota * QUOTA_PRESSURE_THRESHOLD;
    await evictUntil(
      async () => {
        const q = await safeGetQuota();
        return !!q && q.usage > target;
      },
      false,
    );
    await evictUntil(
      async () => {
        const q = await safeGetQuota();
        return !!q && q.usage > target;
      },
      true,
    );
  }
}

async function evictUntil(
  predicate: () => boolean | Promise<boolean>,
  allowProtected: boolean,
): Promise<void> {
  let guard = index.size + 4; // bounded loop just in case
  while (guard-- > 0 && index.size > 0) {
    const should = await predicate();
    if (!should) break;
    const victim = pickLruVictim(allowProtected);
    if (!victim) break;
    const entry = index.get(victim);
    if (!entry) break;
    logger.info('[MediaCache] Evicting LRU entry', {
      id: victim,
      mb: (entry.bytes / 1024 / 1024).toFixed(1),
      protected: protectedIds.has(victim),
      reason: allowProtected ? 'over-budget-after-pass1' : 'over-budget',
    });
    await evictMedia(victim);
  }
}

function pickLruVictim(allowProtected: boolean): string | null {
  for (const key of index.keys()) {
    if (allowProtected || !protectedIds.has(key)) return key;
  }
  return null;
}

async function safeGetQuota(): Promise<{ usage: number; quota: number } | null> {
  try {
    return await platform.storage.getQuota();
  } catch {
    return null;
  }
}

// ── Periodic quota probe ───────────────────────────────────────────

let quotaCheckTimer: ReturnType<typeof setInterval> | null = null;

function startQuotaProbe(): void {
  if (quotaCheckTimer) return;
  quotaCheckTimer = setInterval(() => {
    void enforceLimits().catch((err) =>
      logger.warn('[MediaCache] quota probe failed', { err: String(err) }),
    );
  }, QUOTA_RECHECK_INTERVAL_MS);
}

/**
 * Scheduler calls this on every plan apply with the set of media IDs
 * that should NOT be evicted — currently-playing + about-to-play. The
 * cache still LRU-evicts inside this set if absolutely necessary, but
 * only as a last resort after exhausting everything else.
 */
export function setProtectedMedia(ids: Iterable<string>): void {
  protectedIds = new Set(ids);
}

// ── Stats ──────────────────────────────────────────────────────────

export function getCacheStats(): {
  count: number;
  bytes: number;
  bytesLimit: number;
  utilization: number;
  platform: string;
} {
  return {
    count: index.size,
    bytes: totalBytes,
    bytesLimit: MAX_CACHE_BYTES,
    utilization: totalBytes / MAX_CACHE_BYTES,
    platform: platform.kind,
  };
}

export function getCacheSize(): number {
  return index.size;
}

/**
 * Wipe every cached media entry — used by the diagnostic screen's
 * "Clear cache" action. Force-revoke Blob URLs as we go so the browser
 * frees the underlying buffers immediately, and clear the in-memory
 * index after disk eviction completes.
 */
export async function clearAllCachedMedia(): Promise<void> {
  const ids = [...index.keys()];
  for (const id of ids) {
    try {
      await evictMedia(id);
    } catch (err) {
      logger.warn('[MediaCache] clearAll: evict failed', {
        id,
        err: String(err),
      });
    }
  }
  logger.info('[MediaCache] cache cleared', { evicted: ids.length });
}
